/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren, Ref, JSX } from 'preact';
import { CSSProperties, forwardRef } from 'preact/compat';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { ColorProps } from '#utils/UNSAFE_interpolations/colors';

type IntrinsicProps = Pick<UnpackSignals<JSX.IntrinsicElements['div']>, 'id' | 'class'>;

type TrackProps = IntrinsicProps & {
  length?: string;
  color?: ColorProps['color'];
  orientation?: 'vertical' | 'horizontal';
  ariaProps?: {
    'aria-valuenow'?: UnpackSignals<JSX.AriaAttributes>['aria-valuenow'];
    'aria-valuemin'?: UnpackSignals<JSX.AriaAttributes>['aria-valuemin'];
    'aria-valuemax'?: UnpackSignals<JSX.AriaAttributes>['aria-valuemax'];
    'aria-valuetext'?: UnpackSignals<JSX.AriaAttributes>['aria-valuetext'];
    'aria-label'?: UnpackSignals<JSX.AriaAttributes>['aria-label'];
    'aria-labelledby'?: UnpackSignals<JSX.AriaAttributes>['aria-labelledby'];
    role?: UnpackSignals<JSX.HTMLAttributes>['role'];
  };
  style?: CSSProperties;
  children?: ComponentChildren;
};

export const BarTrack = forwardRef(
  (
    {
      id,
      length = '100%',
      children,
      color,
      orientation = 'horizontal',
      ariaProps,
      ...otherProps
    }: TrackProps,
    ref?: Ref<HTMLDivElement>
  ) => {
    const isHoriz = orientation === 'horizontal';
    return (
      <div
        id={id}
        ref={ref}
        class={otherProps.class}
        {...ariaProps}
        style={{
          width: isHoriz ? length : undefined,
          height: isHoriz ? undefined : length,
          backgroundColor: color,
          ...otherProps.style
        }}>
        {children}
      </div>
    );
  }
);

type ValueProps = Pick<IntrinsicProps, 'class'> & {
  length?: string;
  size?: string;
  orientation?: 'vertical' | 'horizontal';
  color?: ColorProps['color'];
  left?: string;
  bottom?: string;
  right?: string;
  width?: string;
  height?: string;
};

export const BarValue = ({
  length,
  size = '100%',
  orientation = 'horizontal',
  color,
  left,
  bottom,
  right,
  ...otherProps
}: ValueProps) => {
  const isHoriz = orientation === 'horizontal';
  return (
    <div
      class={otherProps.class}
      style={{
        width: isHoriz ? length : size,
        height: isHoriz ? size : length,
        backgroundColor: color,
        left: left,
        bottom: bottom,
        right: right
      }}></div>
  );
};
